import {Unsubscribe} from 'redux';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as MapsConstants from 'constants/MapsConstants';
import {LayeringMode, SliceComponentName} from 'constants/MapsConstants';
import {Strand} from 'constants/MolbioConstants';
import PolymerAnnotationsView, {PolymerAnnotationsViewModel} from 'molbio/maps/linear/PolymerAnnotationsView';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import * as mapsutil from 'molbio/maps/MapsUtil';
import * as selectables from 'molbio/maps/selectables/Selectables';
import Type from 'molbio/maps/Type';
import Protein from 'molbio/proteins/Protein';
import {ProteinAnnotationVersion} from 'molbio/proteins/util/ProteinConstants';
import * as sequtil from 'molbio/SeqUtil';
import {TabState} from 'Navigation';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {
  getHoveredAnnotationId,
  getSelectedAnnotationId,
  proteinAnnotationUIGlobalState,
  selectAnnotation,
  toggleAnnotationHover,
} from 'toolbar/protein/ProteinUIGlobalState';

export interface ProteinAnnotationViewAttributes extends selectables.SelectableAttributes {
  name: string;
  color: string;
  start: number;
  end: number;
  hover: boolean;
  selected: boolean;
  proteinId: string | null;
  isFromPart?: boolean;
}

/**
 * Note: this is not a view, it's a model.
 */
export class ProteinAnnotationView extends selectables.RegionSelectable<Protein> {
  setting!: string;

  defaults(): Partial<ProteinAnnotationViewAttributes> {
    return {
      // @ts-expect-error
      name: null,
      // @ts-expect-error
      color: null,
      hover: false,
      selected: false,
      proteinId: null,
    };
  }

  // We synchronize annotation hover state to the store;
  // `hover` is then injected into these ProteinAnnotation backbone when
  // ProteinAnnotationsView.getData is called
  _syncHoverStateToStore(_annotation: ProteinAnnotationView, isHovering: boolean): void {
    const proteinId = this.proteinId();
    if (proteinId != null) {
      toggleAnnotationHover(proteinId, this.id, isHovering);
    }
  }

  _syncSelectedStateToStore(_annotation: ProteinAnnotationView, isSelected: boolean): void {
    const proteinId = this.proteinId();
    // We only need to handle the case where the annotation is selected. In practice, the 'selected' attribute
    // on this model never gets set to false - making a range selection handles updating the store
    // and deselecting annotations.
    if (proteinId != null && isSelected) {
      const annotationVersion = {
        id: this.id,
        name: this.name(),
        start: this.start(),
        end: this.end(),
        color: this.color(),
        type: this.get('type'),
        notes: this.get('notes'),
      };
      selectAnnotation(proteinId, annotationVersion);
    }
  }

  initialize(attributes: ProteinAnnotationViewAttributes, options: {syncToStore: boolean}): void {
    super.initialize(attributes, options);
    this.setting = vo.ANNOTATIONS.name;
    if (options.syncToStore) {
      this.on('change:hover', this._syncHoverStateToStore.bind(this));
      this.on('change:selected', this._syncSelectedStateToStore.bind(this));
    }
  }

  name(): ProteinAnnotationViewAttributes['name'] {
    return this.get('name');
  }

  color(): ProteinAnnotationViewAttributes['color'] {
    return this.get('color');
  }

  start(): ProteinAnnotationViewAttributes['start'] {
    return this.get('start');
  }

  end(): ProteinAnnotationViewAttributes['end'] {
    return this.get('end');
  }

  strand(): Strand {
    return 1;
  }

  isFromPart(): boolean {
    if (BenchlingConfig.MOLBIO_ENABLE_AA_PARTS) {
      return this.get('isFromPart') ?? false;
    }
    return false;
  }

  layerStart(): ProteinAnnotationViewAttributes['start'] {
    return this.start();
  }

  layerEnd(): ProteinAnnotationViewAttributes['end'] {
    return this.end();
  }

  uid(): string {
    return this.cid;
  }

  hover(): ProteinAnnotationViewAttributes['hover'] {
    return this.get('hover');
  }

  selected(): ProteinAnnotationViewAttributes['selected'] {
    return this.get('selected');
  }

  proteinId(): ProteinAnnotationViewAttributes['proteinId'] {
    return this.get('proteinId');
  }

  click(protein: Protein, shiftKey: boolean): void {
    if (shiftKey) {
      protein.selection().expandRange(this.start(), this.end());
    } else {
      protein.selection().setEndpoints(this.start(), this.end());
      this.set('selected', true);
    }
  }

  dblClick(protein: Protein): void {
    if (protein.isTransientDisplay()) {
      return;
    }
    analytics.track('protein', 'open_annotation_panel', {
      product: ANALYTICS_MODULE.MOLBIO,
      openedFrom: 'annotation_double_click',
      protein_id: this.proteinId(),
    });
    protein.fileState().get('toolbar').trigger('openProteinAnnotationsPanel');
  }
}

export interface ProteinAnnotationsViewOptions {
  model: PolymerAnnotationsViewModel<Protein>;
  tabstate: TabState;
  visibilityOptions: vo.VisibilityOptions;
  slice: Slice;
  parent: SliceView;
  mapType: string;
  name: SliceComponentName;
}

class ProteinAnnotationsView extends PolymerAnnotationsView<
  Protein,
  PolymerAnnotationsViewModel<Protein>,
  ProteinAnnotationView
> {
  proteinUIGlobalStateUnsubscribe?: Unsubscribe;

  constructor(options: ProteinAnnotationsViewOptions) {
    super(options);
    this.setting = vo.ANNOTATIONS.name;
    this.className = 'proteinAnnotations';
  }

  layeringMode(): LayeringMode {
    // TODO: Temporarily overridden to restrict only to the one supported layeringMode.
    return MapsConstants.LINEAR_LAYER_FULL;
  }

  shouldSyncToStore(): boolean {
    return true;
  }

  isAnnotationHovered(annotation: ProteinAnnotationVersion): boolean {
    const proteinId = this.getProteinId();
    return proteinId != null && getHoveredAnnotationId(proteinId) === annotation.id;
  }

  isAnnotationSelected(annotation: ProteinAnnotationVersion): boolean {
    const proteinId = this.getProteinId();
    return proteinId != null && getSelectedAnnotationId(proteinId) === annotation.id;
  }

  getProteinId(): string | null {
    return this.model.protein().id;
  }

  getAnnotations(): Array<ProteinAnnotationVersion> {
    return this.model.protein().annotations();
  }

  getResiduesType(): Type {
    return this.model.get('layout').AminoAcids.get('type');
  }

  getData(sliceStart: number, sliceEnd: number): Array<ProteinAnnotationView> {
    // Convert POJO annotations (backed by ProteinStore) into Backbone models, and only select ones
    // that are in range for this slice
    const proteinId = this.getProteinId();
    const proteinAnnotations = this.getAnnotations()
      .map((annotation) => {
        // Inject the hover/selected state into the ProteinAnnotation Backbone model
        const hover = this.isAnnotationHovered(annotation);
        const selected = this.isAnnotationSelected(annotation);
        assertAndContinue(
          annotation.start != null && annotation.end != null,
          'Protein annotation must have start and end'
        );
        return new ProteinAnnotationView(
          {
            proteinId,
            hover,
            selected,
            ...annotation,
          },
          {
            syncToStore: this.shouldSyncToStore(),
          }
        );
      })
      .filter((annotation) =>
        sequtil.isRangeOverlap(sliceStart, sliceEnd, annotation.start(), annotation.end())
      );

    // HACK - this generates the layer orders for the annotations. Ideally we would run this once
    // per all annotations rather than once per SliceView
    const modelsAndLayers = mapsutil.getModelLayers(proteinAnnotations, {top: false});
    for (const {model, layer} of modelsAndLayers) {
      model.set(this.layeringMode(), layer, {silent: true});
    }

    return proteinAnnotations;
  }

  _registerAnnotationListeners(): void {
    if (!this.shouldSyncToStore()) {
      return;
    }
    const proteinId = this.getProteinId();
    if (proteinId != null) {
      this.proteinUIGlobalStateUnsubscribe = proteinAnnotationUIGlobalState.subscribeListener(
        proteinId,
        () => {
          this.update();
        }
      );
    }
  }

  stopListening(): void {
    super.stopListening();
    this.proteinUIGlobalStateUnsubscribe?.();
  }
}

export default ProteinAnnotationsView;
